import { useCallback } from "react";
import { styled, css } from "styled-components";
import { mobileBreakPointCSS } from "../defaults/theme.js";

interface Props {
  max?: number;
  value: number;
  onChange: (value: number) => any;
  disabled?: boolean;
  allowZero?: boolean;
}

export const Counter: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  max,
  allowZero,
}) => {
  const handleChange = useCallback(
    (decrement?: boolean) => {
      let newValue = value;

      if (decrement && (allowZero ? value > 0 : value > 1)) {
        --newValue;
      } else if (!decrement) {
        ++newValue;
      }

      if (typeof max === "number" && newValue > max) {
        newValue = max;
      }

      onChange(newValue);
    },
    [value, onChange]
  );

  return (
    <Wrapper $disabled={disabled}>
      <DecreaseButton
        className="b3"
        onClick={() => handleChange(true)}
        aria-label={
          value === 1 && !allowZero
            ? `Du kan ikke reduseres til mindre enn en`
            : `Reduser antall fra ${value} til ${value - 1}`
        }
      >
        -
      </DecreaseButton>
      <CounterInput
        className="b3"
        min={allowZero ? "0" : "1"}
        value={value}
        readOnly
        aria-label={`Du har valgt ${value} av dette produktet`}
      />
      <IncreaseButton
        className="b3"
        onClick={() => handleChange()}
        aria-label={`Øk antall fra ${value} til ${value + 1}`}
      >
        +
      </IncreaseButton>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $disabled: Props["disabled"] }>`
  display: flex;
  gap: ${(props) => props.theme.spacing.xs};

  ${(props) =>
    props.$disabled &&
    css`
      pointer-events: none;
      opacity: 0.6;
    `};
`;

const DecreaseButton = styled.button`
  box-sizing: border-box;
  background-color: #fff;
  color: var(--swatch2);
  width: 42px;
  height: 42px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: 1px solid var(--swatch1);
  border-radius: 5px;
  padding: 0;
  font-size: 24px;
  font-weight: 300;

  @media (min-width: ${mobileBreakPointCSS}) {
    width: 50px;
    height: 50px;
  }
`;

const IncreaseButton = styled.button`
  box-sizing: border-box;
  background-color: #fff;
  color: var(--swatch2);
  width: 42px;
  height: 42px;
  cursor: pointer;
  border: 1px solid var(--swatch1);
  border-radius: 5px;
  padding: 0;
  font-size: 24px;
  font-weight: 300;

  @media (min-width: ${mobileBreakPointCSS}) {
    width: 50px;
    height: 50px;
  }
`;

const CounterInput = styled.input.attrs({ type: "number" })`
  box-sizing: border-box;
  background-color: #fff;
  color: var(--swatch2);
  font-size: 18px;
  font-weight: 600;
  width: 42px;
  height: 42px;
  cursor: pointer;
  text-align: center;
  border: 1px solid var(--swatch1);
  border-radius: 5px;
  padding: 0;
  -moz-appearance: none;
  -webkit-appearance: none;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    width: 50px;
    height: 50px;
  }
`;
